import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Dropdown, Button } from "react-bootstrap";
import { FaCalendarAlt, FaPlus, FaSearch } from "react-icons/fa";
import { AiFillHeart } from "react-icons/ai";
import ProjectCard from "../../components/ProjectCard";
import { useUser } from "../../contextProviders/userContext";
import CreateProjectModal from "../../components/CreateProjectModal";
import api from "../../api";
import AppShell from "../../components/AppShell";
import PageHeader from "../../components/PageHeader";
import "./BrowseProjects.css";

// SDG options
const SDG_OPTIONS = [
  "No Poverty",
  "Zero Hunger",
  "Good Health and Well-being",
  "Quality Education",
  "Gender Equality",
  "Clean Water and Sanitation",
  "Affordable and Clean Energy",
  "Decent Work and Economic Growth",
  "Industry, Innovation and Infrastructure",
  "Reduced Inequalities",
  "Sustainable Cities and Communities",
  "Responsible Consumption and Production",
  "Climate Action",
  "Life Below Water",
  "Life on Land",
  "Peace, Justice and Strong Institutions",
  "Partnerships for the Goals",
];

const interests = [
  "Wits Food Bank",
  "Masidleni Daily Meals",
  "Wits Food Gardens",
  "Wits Student Communal Kitchen",
  "Community Food Programmes",
  "Developing Food Gardens",
  "Urban Waste Management",
  "Reading Clubs",
  "In-person tutoring",
  "Online tutoring",
  "After school assistance",
  "Health and allied assessments",
  "Sports development",
  "Arts and Cultural Development",
  "Animal welfare",
  "Befriending the elderly",
  "Clothing drive",
  "Hygiene drive",
  "Food drives",
  "Social Justice Campaigns",
  "Others",
];

const timePeriods = [
  "All Time",
  "Last Month",
  "Last 3 Months",
  "Last 6 Months",
];

const BrowseProjects = () => {
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const { user } = useUser();
  const [error, setError] = useState(null);
  const [selectedInterest, setSelectedInterest] = useState("All Interests");
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("All Time");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const fetchAllProjects = useCallback(async () => {
    try {
      const response = await api.get("/projects", { params: { view: "browse" } });
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to fetch projects. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllProjects();
  }, [fetchAllProjects]);

  const handleJoinProject = async (projectId, result) => {
    alert(result?.message || "Successfully joined the project!");
    window.location.reload();
  };

  const handleInterestSelect = (interest) => setSelectedInterest(interest);
  const handleTimePeriodSelect = (period) => setSelectedTimePeriod(period);
  const handleSearchChange = (term) => setSearchTerm(term);

  // Filtering logic
  const filteredProjects = projects
    .filter((project) => project.approved === true)
    .filter((project) => project.joinStatus !== "approved")
    .filter((project) => {
      if (selectedInterest !== "All Interests") {
        return project.categories?.includes(selectedInterest);
      }
      return true;
    })
    .filter((project) => {
      if (selectedTimePeriod !== "All Time") {
        const now = new Date();
        let cutoffDate = new Date();
        switch (selectedTimePeriod) {
          case "Last Month":
            cutoffDate.setMonth(now.getMonth() - 1);
            break;
          case "Last 3 Months":
            cutoffDate.setMonth(now.getMonth() - 3);
            break;
          case "Last 6 Months":
            cutoffDate.setMonth(now.getMonth() - 6);
            break;
          default:
            break;
        }
        return new Date(project.startDate) >= cutoffDate;
      }
      return true;
    })
    .filter((project) => {
      if (searchTerm.trim() !== "") {
        const searchLower = searchTerm.toLowerCase();
        return (
          project.name?.toLowerCase().includes(searchLower) ||
          project.description?.toLowerCase().includes(searchLower) ||
          project.organiserName?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  return (
    <AppShell>
      <PageHeader
        title="Browse projects"
        description={`Find your next volunteering opportunity${user?.name ? `, ${user.name}` : ""}.`}
        actions={<Button onClick={() => setShowModal(true)}><FaPlus className="me-2" />Propose project</Button>}
      />

      <section className="browse-project-controls" aria-label="Project filters">
        <div className="browse-project-search">
          <FaSearch aria-hidden="true" />
          <label className="visually-hidden" htmlFor="browse-project-search">Search projects</label>
          <input
            id="browse-project-search"
            type="search"
            value={searchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
            placeholder="Search projects"
          />
        </div>
        <div className="browse-project-filters">
              <Dropdown onSelect={handleTimePeriodSelect}>
                <Dropdown.Toggle
                  variant="outline-primary">
                  <FaCalendarAlt className="me-1" />
                  {selectedTimePeriod}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {timePeriods.map((period) => (
                    <Dropdown.Item key={period} eventKey={period}>
                      {period}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown onSelect={handleInterestSelect}>
                <Dropdown.Toggle
                  variant="outline-primary">
                  <AiFillHeart className="me-1" />
                  {selectedInterest}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ maxHeight: "200px", overflowY: "auto" }}>
                  <Dropdown.Item eventKey="All Interests">
                    All Interests
                  </Dropdown.Item>
                  {interests.map((interest) => (
                    <Dropdown.Item key={interest} eventKey={interest}>
                      {interest}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
        </div>
        <span className="browse-project-count">{filteredProjects.length} available</span>
      </section>

      {error && <div className="alert">{error}</div>}
      {loading ? (
        <div className="browse-project-empty">Loading projects…</div>
      ) : (
        <Row xs={1} sm={2} xl={3} className="g-3 browse-project-grid">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <Col key={project._id}>
                <ProjectCard
                  project={project}
                  isJoined={project.joinStatus === "approved"}
                  onJoin={(result) => handleJoinProject(project._id, result)}
                  isFlippable={true}
                />
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <div className="browse-project-empty">No projects match the selected filters.</div>
            </Col>
          )}
        </Row>
      )}

      <CreateProjectModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onProjectCreated={(newProject) => setProjects([...projects, newProject])}
        interests={interests}
        sdgOptions={SDG_OPTIONS}
      />
    </AppShell>
  );
};

export default BrowseProjects;
